/**
 * Mounts the one socket of the tab while a session is open, and exposes it to hooks
 * (ADR 0029). Sits inside `TenantProvider` and above the router, so every screen can
 * subscribe and nothing runs before the tenant is known.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { sessionKey, useSession } from "@/core/auth/useSession";
import { teamsKey } from "@/core/teams/api";
import { CLOSE_UNAUTHENTICATED, type ServerFrame } from "./protocol";
import { RealtimeSocket } from "./socket";

const RealtimeContext = createContext<RealtimeSocket | null>(null);

export function RealtimeProvider({
  children,
  socket: given,
}: {
  children: ReactNode;
  /** Tests hand in their own instance; the app builds one. */
  socket?: RealtimeSocket;
}) {
  const session = useSession();
  const qc = useQueryClient();
  const authenticated = session.data?.authenticated === true;
  const [socket] = useState(() => given ?? new RealtimeSocket());

  useEffect(() => {
    if (!authenticated) return;
    const offResync = socket.onResync(() => {
      // Whatever a lost connection may have swallowed: cheap to ask again.
      for (const key of [["attempts"], ["leaderboard"], ["stats"], ["chat"], [...teamsKey]]) {
        void qc.invalidateQueries({ queryKey: key });
      }
    });
    const offClose = socket.onServerClose((code) => {
      if (code === CLOSE_UNAUTHENTICATED) void qc.invalidateQueries({ queryKey: sessionKey });
    });
    socket.start();
    return () => {
      offResync();
      offClose();
      socket.stop();
    };
  }, [authenticated, socket, qc]);

  return <RealtimeContext.Provider value={socket}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeSocket(): RealtimeSocket | null {
  return useContext(RealtimeContext);
}

const noopSubscribe = () => () => undefined;

/** True while the socket is open: the moment polling can stand down. */
export function useRealtimeOpen(): boolean {
  const socket = useContext(RealtimeContext);
  return useSyncExternalStore(
    socket ? socket.subscribeStatus : noopSubscribe,
    () => socket?.getStatus() === "open",
    () => false,
  );
}

/** Subscribes to `topic` for the life of the component; `null` subscribes to nothing. */
export function useTopic(topic: string | null, handler: (frame: ServerFrame) => void): void {
  const socket = useContext(RealtimeContext);
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    if (!socket || !topic) return;
    return socket.subscribe(topic, (frame) => latest.current(frame));
  }, [socket, topic]);
}
